/** @format */

import {ArrowSmDownIcon, ArrowSmUpIcon} from '@heroicons/react/solid'
import * as Sentry from '@sentry/react'
import clsx from 'clsx'
import React, {Fragment, useContext, useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'

import {Alert} from '@src/components/tailwind/Alert'
import {getAppLogoByPluginKind} from '@src/components/tailwind/AppLogo'
import {PromoBanner} from '@src/components/tailwind/PromoBanner/PromoBanner'

import {minimumUpgradeProduct} from '@src/contexts/ProductsContext'
import {PLUGIN_CONFIGS} from '@src/plugins/pluginConfigs'

import {AccountContext} from '../contexts/AccountContext'
import formspreeStarSvg from '../images/formspree-star-faded.svg'
import {
  featureTrialMinimumProduct,
  getProduct,
  isLegacyPlan,
  isTrialPlan,
} from '../utils'
import {
  CheckoutButton,
  CheckoutContext,
  CheckoutContextProvider,
} from './CheckoutContext'
import {
  CARD_FEATURES,
  CARD_LIMITS,
  FEATURE_SECTION_ID,
  FEATURE_TABLE_SECTIONS,
  PLUGIN_DISPLAY_OVERRIDES,
  PLUGIN_SECTION_ID,
  PLUGIN_TABLE_SECTIONS,
  PLUGIN_TABLE_SUBTITLES,
  SHOW_FREE,
} from './constants'
import {
  applyDisplayOverrides,
  getRowValueForProduct,
  isPlanInProduct,
  thousandsWithOneDecimal,
} from './utils'

const formspree = window.formspree

const INITIAL_STATE = {
  currentPlanDetail: 0,
  currentfeatDetail: '',
}

// Styles for the row a ?feature= or ?plugin= link points at: bold text, a blue
// name, and a light blue border on three sides.
//
// Two things about the borders that are easy to get wrong:
//
//   - They go on the cells, not the <tr>. The desktop table is
//     border-collapse:separate, which ignores borders set on a row.
//   - They name one side each (border-t-*, not border-*), because a marked cell
//     can also carry the table's own border-gray-200, which sets all four.
//
// A marked row is two <tr>s: the name, then a description that is always in the
// DOM and collapses to nothing when closed. Top border on the first, bottom on
// the second, so the pair is boxed in whether or not it is open.
const MARKED_TEXT = 'font-semibold'
const MARKED_NAME = `${MARKED_TEXT} text-blue-700 decoration-blue-300`
const MARKED_TOP = 'border-t-2 border-t-blue-100'
const MARKED_BOTTOM = 'border-b-2 border-b-blue-100'
const MARKED_LEFT = 'border-l-2 border-l-blue-100'

// The subheading names two things, a plan and a row. Both are set this way, so
// neither looks more like a link than the other.
const NAMED_IN_SUBHEADING = 'font-semibold text-blue-700'

// Table density. The feature table renders 'compact'; the plugin table
// 'tight', it having far more rows.
//
//   row    — padding for table rows
//   group  — padding for section headings
//   text   — size of section headings
//   border — the gap above a section
//
// The gap comes from a border rather than padding because `group` padding sits
// inside the heading's grey band: padding would just make the band taller,
// where a border draws outside it and reads as whitespace.
const DENSITY = {
  compact: {
    row: 'py-3',
    group: 'pb-1 pt-2',
    text: 'text-lg',
    border: 'border-t-[20px] border-white',
  },
  tight: {
    row: 'py-2',
    group: 'py-1.5',
    text: 'text-base',
    border: 'border-t border-gray-200',
  },
}

const PlansPage = () => {
  const products = formspree.products.filter(i => i.available)
  const features = formspree.features

  const {account, sub} = useContext(AccountContext)

  const [state, setState] = useState({...INITIAL_STATE})
  const {checkoutData, updateCheckoutData} = useContext(CheckoutContext)
  const [searchParams] = useSearchParams()
  // The three query params the page reads:
  //   ?recommended=<product id>  which plan card gets the pill
  //   ?feature=<feature id>      which comparison-table row to mark
  //   ?plugin=<plugin kind>      which plugin-table row to mark
  //
  // Read in an effect rather than during render, because /plans is prerendered
  // at build time (see formspree/js/prerender/) with no query string. Reading
  // them during render would not match that markup, and React would throw the
  // markup away as a hydration mismatch.
  //
  // If a URL somehow has both ?feature= and ?plugin=, the plugin wins: an
  // account can have the feature but not the plugin, so there is no single
  // right thing to say about the two together.
  const [recommendedProductId, setRecommendedProductId] = useState(null)
  const [markedFeatureId, setMarkedFeatureId] = useState(null)
  const [markedPluginKind, setMarkedPluginKind] = useState(null)
  useEffect(() => {
    const plugin = searchParams.get('plugin')
    setRecommendedProductId(searchParams.get('recommended'))
    setMarkedPluginKind(plugin)
    setMarkedFeatureId(plugin === null ? searchParams.get('feature') : null)
  }, [searchParams])

  // Each plan "card" lists only the features cheaper plan cards didn't
  // already list — "everything in <cheaper plan>, plus these". Tracked by
  // id, not position, so it holds whatever order CARD_FEATURES is in.
  const listedFeatureIds = new Set()

  // FEATURES
  const featuresById = new Map(Object.values(features).map(f => [f.id, f]))
  const featureSections = FEATURE_TABLE_SECTIONS.map(([group, ids]) => [
    group,
    ids
      .map(id => featuresById.get(id))
      .filter(Boolean)
      .map(applyDisplayOverrides),
  ])

  // PLUGINS
  // Plugin rows are shaped like feature rows so the table renders them the same
  // way; getRowValueForProduct keys off pluginKind to resolve availability.
  const pluginsByKind = new Map(PLUGIN_CONFIGS.map(p => [p.kind, p]))
  const pluginSections = PLUGIN_TABLE_SECTIONS.map(([label, kinds]) => [
    label,
    kinds
      .map(kind => pluginsByKind.get(kind))
      .filter(Boolean)
      .map(plugin => ({
        id: `plugin-${plugin.kind}`,
        pluginKind: plugin.kind,
        name: plugin.name,
        description: plugin.description,
        // upgradeRequires wins where a plugin has one: Stripe works on any
        // plan but is sold on Professional, and the row says what it is sold on.
        requires: plugin.upgradeRequires ?? plugin.requires,
        ...PLUGIN_DISPLAY_OVERRIDES[plugin.kind],
      })),
  ]).filter(([, rows]) => rows.length > 0)

  // Plugin rows carry a pluginKind; feature rows match on their own id.
  const isMarked = feat =>
    feat.pluginKind
      ? feat.pluginKind === markedPluginKind
      : feat.id === markedFeatureId

  // One row at most: each param names one, and only one param is used.
  const markedRow = [...featureSections, ...pluginSections]
    .flatMap(([, rows]) => rows)
    .find(isMarked)

  // Which feature decides the plan we name. Taken from the marked row first, so
  // the plan matches what the table shows; from ?feature= when the table has no
  // row for it.
  //
  // Those row-less features are 'export' and 'search_submissions' (commented out
  // of FEATURE_TABLE_SECTIONS) and 'plugins'/'premium_plugins' (left to the
  // plugin table). Without the fallback their links would lose the pill.
  // ?plugin= needs no fallback: constants.test.jsx checks the plugin table
  // covers the whole catalogue.
  //
  // Never read the plan from the URL itself. This calls the same
  // minimumUpgradeProduct the link used to write its own copy, so the two always
  // agree, and a hand-made ?feature=X&recommended=Y cannot make the page
  // advertise a plan the feature does not need.
  const gatingFeatureId =
    markedRow !== undefined
      ? (markedRow.requires ?? markedRow.id)
      : (markedFeatureId ?? undefined)
  const derivedProduct =
    gatingFeatureId === undefined
      ? undefined
      : minimumUpgradeProduct(formspree.products, gatingFeatureId)

  const markedName = markedRow?.name

  // Which plan card gets the pill. The derived one whenever a feature or plugin
  // was named, so the pill and the wording never point at different plans.
  // ?recommended= only gets a say when neither was named.
  const namedProduct =
    gatingFeatureId !== undefined
      ? derivedProduct
      : products.find(p => p.id === recommendedProductId)

  const accountPlanProduct = formspree.products.find(
    prod => prod.id === account?.product,
  )

  // Drop the pill when it would land on a plan cheaper than the reader's own,
  // which reads as a suggestion to downgrade. Links outlive the plan they were
  // written for: bookmarked, shared, or clicked after upgrading. Their own plan
  // is fine to point at.
  //
  // Except when the subscription is set to cancel. They drop to Free at the end
  // of the period, so cheaper plans are real choices again.
  const keepingCurrentPlan = !sub?.cancel_at_period_end
  const pillProductId =
    keepingCurrentPlan &&
    accountPlanProduct !== undefined &&
    namedProduct !== undefined &&
    namedProduct.monthly.price < accountPlanProduct.monthly.price
      ? undefined
      : namedProduct?.id

  // Two ways to already have the feature, worded differently below: their plan
  // includes it, or it was granted to the account on its own. Crediting the plan
  // for a grant would be false.
  const planIncludesIt =
    gatingFeatureId !== undefined &&
    Boolean(
      accountPlanProduct?.monthly.features.some(
        feat => feat.id === gatingFeatureId,
      ),
    )
  const grantIncludesIt =
    gatingFeatureId !== undefined &&
    Boolean(account?.features?.[gatingFeatureId])
  const accountAlreadyHasIt = planIncludesIt || grantIncludesIt

  // Which of the four subheadings to show:
  //
  //   upgrade   signed in, lacks it        "Upgrade to X to get Y"
  //   included  signed out                 "Y is included in X and up"
  //   entitled  signed in, already has it  "Your X plan includes Y"
  //   default   no plan resolved           the generic subtitle
  const subheadingMode =
    derivedProduct === undefined || markedName === undefined
      ? 'default'
      : accountAlreadyHasIt
        ? 'entitled'
        : account
          ? 'upgrade'
          : 'included'

  const emphasize = text => <span className={NAMED_IN_SUBHEADING}>{text}</span>

  const ACTIVE_TOGGLE = ' bg-white border-b-2 border-gray-200'
  const INACTIVE_TOGGLE = ' z-10 text-gray-600'

  const setCurrentFeatureDetail = feat => e => {
    setState(s => {
      if (s.currentfeatDetail === feat.name) {
        return {...s, currentfeatDetail: ''}
      } else {
        return {
          ...s,
          currentfeatDetail: feat.name,
        }
      }
    })
  }

  // Smooth-scroll the in-page anchors rather than letting the browser
  // jump. Kept local to these links instead of setting scroll-behavior
  // globally, and falls back to an instant jump when the visitor prefers
  // reduced motion.
  const scrollTo = (target, block) => {
    const reducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches
    target.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block,
    })
  }

  const scrollToAnchor = anchor => event => {
    const target = document.getElementById(anchor)
    if (!target) {
      return
    }
    event.preventDefault()
    scrollTo(target, 'start')
    // keep the URL shareable, without triggering a second instant jump
    window.history.replaceState(null, '', `#${anchor}`)
  }

  // The marked row exists twice, once per table layout, and only one layout is
  // on screen at a time — so scroll to whichever is visible. If neither is
  // (jsdom, where nothing has a layout), leave it to the anchor's href.
  const scrollToMarkedRow = event => {
    const rows = [...document.querySelectorAll('[data-marked-row]')]
    const row = rows.find(el => el.offsetParent !== null) ?? rows[0]
    if (!row) {
      return
    }
    event.preventDefault()
    scrollTo(row, 'center')
  }

  // The row name in the subheading, which scrolls to the row on click. The page
  // marks the row where it sits rather than scrolling on arrival, so this is how
  // the reader gets there. Its href points at the table the row lives in, which
  // is the fallback and what a middle-click or a copied link gives you.
  const markedRowLink = (
    <a
      className={NAMED_IN_SUBHEADING}
      href={`#${markedRow?.pluginKind ? PLUGIN_SECTION_ID : FEATURE_SECTION_ID}`}
      onClick={scrollToMarkedRow}
    >
      {markedName}
    </a>
  )

  // Landing on /plans#plugins: the browser tries the fragment during load,
  // while this page is still an empty root, so there is nothing to scroll
  // to and it does not retry. Redo it once mounted. Instant rather than
  // smooth, since this stands in for the jump the browser would have made.
  useEffect(() => {
    const anchor = window.location.hash.slice(1)
    if (!anchor) {
      return
    }
    document.getElementById(anchor)?.scrollIntoView({block: 'start'})
  }, [])

  // Rendered above the plan cards and again above the plugin table. Both copies
  // read and write the same checkoutData.period, so they stay in sync.
  // 'large' for the toggle above the plan cards, the page's primary control;
  // the two above the comparison tables stay at the default size.
  const TOGGLE_SIZE = {
    default: {track: 'p-0.5', button: 'py-2 text-sm sm:px-8'},
    large: {track: 'p-1', button: 'py-3 text-base sm:px-10'},
  }

  const renderBillingPeriodToggle = (className, size = 'default') => (
    <div
      className={clsx(
        'relative flex rounded-lg bg-gray-100',
        TOGGLE_SIZE[size].track,
        className,
      )}
    >
      {[
        ['monthly', 'Monthly billing'],
        ['yearly', 'Yearly billing'],
      ].map(([period, label]) => (
        <button
          key={period}
          type="button"
          className={clsx(
            'relative w-1/2 whitespace-nowrap rounded-lg font-bold focus:outline-none sm:w-auto',
            TOGGLE_SIZE[size].button,
            checkoutData.period === period ? ACTIVE_TOGGLE : INACTIVE_TOGGLE,
          )}
          onClick={() => {
            updateCheckoutData('period', period)
          }}
        >
          {label}
        </button>
      ))}
    </div>
  )

  // Sits between a table's heading and the table itself, so the billing period
  // is next to the prices it changes.
  //
  // The toggle shrink-wraps as a flex item, which squeezes its
  // whitespace-nowrap labels on narrow screens, so let it span the width below
  // sm — as the toggle above the plan cards already does.
  const renderTableBillingToggle = () => (
    <div className="mb-6 flex justify-center px-4">
      {renderBillingPeriodToggle('w-full sm:w-auto')}
    </div>
  )

  // The logo the form editor uses, at text size. Feature rows have none, and a
  // few plugins have none either, so the slot is fixed-width to keep the names
  // in a line.
  const rowLogo = row => {
    if (row.pluginKind === undefined) {
      return null
    }
    const Logo = getAppLogoByPluginKind(row.logoKind ?? row.pluginKind)
    return (
      <span className="inline-flex w-5 shrink-0 justify-center">
        {Logo && <Logo aria-hidden="true" fontSize="1rem" />}
      </span>
    )
  }

  const renderProductPrice = prod => (
    <p className="mt-4">
      <span className="text-4xl font-extrabold text-gray-900">
        {prod.monthly.price === Infinity
          ? 'Custom'
          : checkoutData.period === 'monthly'
            ? `$${prod.monthly.price}`
            : `$${prod.yearly ? prod.yearly.price / 12 : prod.monthly.price}`}
      </span>
      {prod.monthly.price !== Infinity && (
        <span className="text-base font-medium text-gray-500"> /mo</span>
      )}
    </p>
  )

  // A limit's suffix renders smaller and greyer beside the label, as "/mo" does
  // on the submissions row.
  const limitLabel = limit => (
    <>
      {limit.shortname || limit.name}
      {limit.suffix && (
        <span className="text-sm font-medium text-gray-500">
          {' '}
          {limit.suffix}
        </span>
      )}
    </>
  )

  const renderProduct = prod => {
    const featureById = id => prod.monthly.features.find(f => f.id === id)
    const limits = CARD_LIMITS.map(featureById)
      .filter(f => f && f.limit !== undefined)
      .map(applyDisplayOverrides)
    const cardFeatures = CARD_FEATURES.map(featureById)
      .filter(f => f && f.limit === undefined)
      .map(applyDisplayOverrides)
    const isRecommended = prod.id === pillProductId
    return (
      <div
        className={clsx(
          'relative divide-y divide-gray-200 rounded-2xl border bg-white shadow-sm',
          isRecommended
            ? 'border-blue-700 ring-1 ring-blue-700'
            : 'border-gray-200',
        )}
        key={prod.id}
      >
        <div className="p-6">
          {isRecommended && (
            <p className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-700 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
              Recommended
            </p>
          )}
          <h2 className="text-lg font-bold leading-6 text-gray-900">
            {prod.name.toUpperCase()}
          </h2>

          <p
            className="my-4 h-8 text-sm text-gray-600"
            dangerouslySetInnerHTML={{__html: prod.description}}
          />

          {renderProductPrice(prod)}

          <div className="pt-8">
            <ul className="space-y-4">
              {limits.map(limit => (
                <li className="flex space-x-3" key={limit.name}>
                  {limit.limit ? (
                    <span className="text-gray-700">
                      <strong>
                        {typeof limit.limit === 'number' && limit.prefix}
                        {thousandsWithOneDecimal(limit.limit)}
                        {limit.shortunits || limit.units || ''}
                      </strong>{' '}
                      {limitLabel(limit)}
                    </span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <CheckoutButton prod={prod} />
        </div>

        {cardFeatures.length > 0 && (
          <div className="px-6 pb-8 pt-6">
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-900">
              {getFeaturesPlusTitle(prod)}
            </h3>

            <ul className="mt-6 space-y-4">
              {cardFeatures.map(feature => {
                if (listedFeatureIds.has(feature.id)) return null
                listedFeatureIds.add(feature.id)
                return (
                  <li className="flex space-x-3" key={feature.name}>
                    <svg
                      className="h-5 w-5 shrink-0 text-green-500"
                      x-description="Heroicon name: check"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="text-gray-600">
                      {feature.anchor ? (
                        <a
                          className="underline decoration-gray-300 decoration-dashed decoration-2 underline-offset-[3px] hover:text-primary"
                          href={`#${feature.anchor}`}
                          onClick={scrollToAnchor(feature.anchor)}
                        >
                          {feature.shortname || feature.name}
                        </a>
                      ) : (
                        feature.shortname || feature.name
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    )
  }

  // Rendered once for the feature sections and again for the plugin sections.
  //
  // buttonPrefix keys the checkout modals, not the button ids — CheckoutContext
  // opens whichever modal matches `prefix + prod.id`. Give both instances the
  // same prefix and clicking one button opens every matching copy's modal.
  const renderMobileComparisonTable = (
    sections,
    {buttonPrefix = 'mobile-', density = 'compact', subtitles = {}} = {},
  ) => {
    const columns = products.filter(prod => !prod.hidden)
    const {
      row: rowPadding,
      group: groupPadding,
      text: groupText,
      border: groupBorder,
    } = DENSITY[density]

    const productTitle = prod => (
      <>
        <h2
          className={clsx(
            'text-lg font-bold uppercase leading-6 tracking-wide',
            prod.id === pillProductId ? 'text-blue-700' : 'text-gray-900',
          )}
        >
          {prod.name}
        </h2>
        {renderProductPrice(prod)}
        {subtitles[prod.id] ? (
          <p className="mt-4 text-gray-500">{subtitles[prod.id]}</p>
        ) : (
          <p
            className="mt-4 text-gray-500"
            dangerouslySetInnerHTML={{__html: prod.description}}
          />
        )}
      </>
    )

    const arrowButton = (icon, enabled, increment) => (
      <div
        className={clsx(
          'scale-x-50 text-5xl',
          enabled(state.currentPlanDetail)
            ? 'cursor-pointer text-gray-600'
            : 'cursor-default text-gray-200',
        )}
        onClick={() => {
          setState(prev => ({
            ...prev,
            currentPlanDetail: enabled(prev.currentPlanDetail)
              ? increment(prev.currentPlanDetail)
              : prev.currentPlanDetail,
          }))
        }}
      >
        {icon}
      </div>
    )

    const featureGroupHeader = group => (
      <tr>
        <th
          className={clsx(
            'bg-gray-50 px-4 text-left font-bold text-gray-900',
            groupBorder,
            groupPadding,
            groupText,
          )}
          colSpan={2}
        >
          {group}
        </th>
        <th className="sr-only" scope="col">
          {group}
        </th>
      </tr>
    )

    const featureName = feat => (
      <span className="inline-flex items-center gap-2">
        {rowLogo(feat)}
        <span
          className={clsx(
            'cursor-pointer underline decoration-dashed decoration-2 underline-offset-[3px]',
            isMarked(feat) ? MARKED_NAME : 'font-normal decoration-gray-300',
          )}
          onClick={setCurrentFeatureDetail(feat)}
        >
          {feat.name}
        </span>
      </span>
    )

    const featureDesc = feat => (
      <tr key={`${feat.name}-footer`}>
        <td
          className={clsx(
            'px-4 text-left',
            isMarked(feat) && `${MARKED_LEFT} ${MARKED_BOTTOM}`,
          )}
          colSpan={2}
        >
          <div
            className={clsx(
              'grid transition-all duration-200 ease-in-out',
              state.currentfeatDetail === feat.name
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div className="overflow-hidden">
              <div
                className="py-3"
                dangerouslySetInnerHTML={{__html: feat.description}}
              />
            </div>
          </div>
        </td>
      </tr>
    )

    return (
      <div className="mx-auto lg:hidden">
        <div className="sticky top-0 z-10 overflow-hidden bg-white">
          <div
            className="flex"
            style={{
              transform: `translate3d(-${state.currentPlanDetail * 100}%, 0, 0)`,
            }}
          >
            {columns.map(prod => (
              <div
                className="mb-6 mt-4 shrink-0 grow-0 basis-full px-4"
                key={prod.id}
              >
                <div className="flex w-full items-center">
                  {arrowButton(
                    <>&#9664;</>,
                    i => i > 0,
                    i => i - 1,
                  )}
                  <div className="center grow">{productTitle(prod)}</div>
                  {arrowButton(
                    <>&#9654;</>,
                    i => i < columns.length - 1,
                    i => i + 1,
                  )}
                </div>
                <CheckoutButton
                  prod={prod}
                  prefix={buttonPrefix}
                  noMobileScroll={true}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform ease-in-out"
            style={{
              transform: `translate3d(-${state.currentPlanDetail * 100}%, 0, 0)`,
            }}
          >
            {columns.map(prod => (
              <table className="shrink-0 grow-0 basis-full" key={prod.id}>
                <tbody>
                  {sections.map(([group, featurelist]) => (
                    <Fragment key={group}>
                      {featureGroupHeader(group)}
                      {featurelist.map(feat => (
                        <Fragment key={feat.name}>
                          <tr
                            className={clsx(
                              'border-t border-gray-200',
                              isMarked(feat) && MARKED_TEXT,
                            )}
                            data-marked-row={
                              isMarked(feat) ? 'true' : undefined
                            }
                          >
                            <td
                              className={clsx(
                                'px-4 text-left',
                                isMarked(feat) &&
                                  `${MARKED_LEFT} ${MARKED_TOP}`,
                                rowPadding,
                              )}
                            >
                              {featureName(feat)}
                            </td>
                            <td
                              className={clsx(
                                'pr-4 text-left',
                                isMarked(feat) && MARKED_TOP,
                                rowPadding,
                              )}
                            >
                              {getRowValueForProduct(feat, prod)}
                            </td>
                          </tr>
                          {featureDesc(feat)}
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // As above: rendered for features and again for plugins. Each instance needs
  // its own button prefixes so all four sets of checkout buttons get distinct
  // ids. showFooter controls the repeat row of buttons at the very bottom.
  const renderDesktopComparisonTable = (
    sections,
    {
      caption = 'Pricing plan comparison',
      showPricing = true,
      showFooter = true,
      buttonPrefix = 'table-header-',
      footerButtonPrefix = 'table-footer-',
      density = 'compact',
      subtitles = {},
    } = {},
  ) => {
    const {
      row: rowPadding,
      group: groupPadding,
      text: groupText,
      border: groupBorder,
    } = DENSITY[density]
    const columns = products.filter(prod => !prod.hidden)
    const header = () =>
      columns.map(prod => (
        <th
          className={clsx(
            'sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 text-left text-lg font-bold uppercase leading-6 tracking-wide',
            prod.id === pillProductId ? 'text-blue-700' : 'text-gray-900',
          )}
          key={prod.name}
        >
          {prod.name}
        </th>
      ))

    const prices = () =>
      columns.map(prod => (
        <td className="h-full px-6 pt-4 align-top" key={prod.id}>
          {renderProductPrice(prod)}
          <p className="mt-4 text-sm text-gray-500">
            {subtitles[prod.id] ?? prod.description}
          </p>
        </td>
      ))

    const checkoutButtons = () =>
      columns.map(prod => (
        <td className="h-full px-6 pb-8 align-top" key={prod.id}>
          <CheckoutButton prod={prod} prefix={buttonPrefix} narrow={true} />
        </td>
      ))

    const availability = feat =>
      columns.map(prod => (
        <td
          className={clsx(
            'px-6',
            isMarked(feat) ? MARKED_TOP : 'border-t border-gray-200',
            rowPadding,
          )}
          key={prod.id}
        >
          {getRowValueForProduct(feat, prod)}
        </td>
      ))

    const description = feat => (
      <tr key={`${feat.name}-desc`}>
        <td
          className={clsx(
            'px-6',
            isMarked(feat) && `${MARKED_LEFT} ${MARKED_BOTTOM}`,
          )}
          colSpan={columns.length + 1}
        >
          <div
            className={clsx(
              'grid transition-all duration-200 ease-in-out',
              state.currentfeatDetail === feat.name
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div className="overflow-hidden">
              <div
                className={rowPadding}
                dangerouslySetInnerHTML={{__html: feat.description}}
              />
            </div>
          </div>
        </td>
      </tr>
    )

    return (
      <div className="hidden lg:block">
        <table
          className="relative h-px w-full table-fixed border-separate"
          cellPadding="0"
          cellSpacing="0"
        >
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <th
                className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 text-left text-sm font-medium text-gray-900"
                scope="col"
              >
                <span className="sr-only">Feature by</span>
                <span className="sr-only">Plans</span>
              </th>
              {header()}
            </tr>
          </thead>
          <tbody>
            {showPricing && (
              <>
                <tr>
                  <th className="sr-only" scope="row">
                    Pricing
                  </th>
                  {prices()}
                </tr>
                <tr>
                  <th className="sr-only" scope="row">
                    Checkout Buttons
                  </th>
                  {checkoutButtons()}
                </tr>
              </>
            )}

            {sections.map(([group, featurelist]) => (
              <Fragment key={group}>
                <tr>
                  <th
                    className={clsx(
                      'bg-gray-50 pl-6 text-left font-bold text-gray-900',
                      groupBorder,
                      groupPadding,
                      groupText,
                    )}
                    colSpan={columns.length + 1}
                    scope="colgroup"
                  >
                    {group}
                  </th>
                </tr>
                {featurelist.map(feat => (
                  <Fragment key={feat.id}>
                    <tr
                      className={isMarked(feat) ? MARKED_TEXT : undefined}
                      data-marked-row={isMarked(feat) ? 'true' : undefined}
                    >
                      <th
                        className={clsx(
                          'px-6 text-left',
                          isMarked(feat)
                            ? `${MARKED_TOP} ${MARKED_LEFT} ${MARKED_TEXT}`
                            : 'border-t border-gray-200 font-normal',
                          rowPadding,
                        )}
                        scope="row"
                      >
                        <span className="inline-flex items-center gap-2">
                          {rowLogo(feat)}
                          <span
                            className={clsx(
                              'cursor-pointer underline decoration-dashed decoration-2 underline-offset-[3px]',
                              isMarked(feat)
                                ? MARKED_NAME
                                : 'decoration-gray-300',
                            )}
                            onClick={setCurrentFeatureDetail(feat)}
                          >
                            {feat.name}
                          </span>
                        </span>
                      </th>
                      {availability(feat)}
                    </tr>
                    {description(feat)}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </tbody>
          {showFooter && (
            <tfoot>
              <tr className="border-t border-gray-200">
                <th className="sr-only" scope="row">
                  Choose your plan
                </th>

                {columns.map(prod => (
                  <td className="px-6 pt-5" key={prod.id}>
                    <CheckoutButton
                      prod={prod}
                      prefix={footerButtonPrefix}
                      narrow={true}
                    />
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    )
  }

  const renderComparisonTables = () => {
    return (
      // scroll-mt keeps the heading clear of the sticky plan-name header
      <div className="scroll-mt-24 bg-white" id={FEATURE_SECTION_ID}>
        <p className="mb-4 mt-20 px-4 text-center">
          <a
            className="inline-flex items-center gap-1 text-sm text-gray-600 underline decoration-gray-300 decoration-dashed decoration-2 underline-offset-[3px] hover:text-primary"
            href={`#${PLUGIN_SECTION_ID}`}
            onClick={scrollToAnchor(PLUGIN_SECTION_ID)}
          >
            Jump to plugin comparison
            <ArrowSmDownIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        </p>
        <h1 className="mb-4 px-4 text-center text-4xl font-extrabold text-gray-900">
          Detailed Feature Comparison
        </h1>
        <p className="mb-8 px-4 text-center text-lg text-gray-600">
          Need customization? Need to add your team? We got you.
        </p>
        {renderTableBillingToggle()}
        <div className="mx-auto mb-16 bg-white lg:max-w-7xl lg:px-8">
          {renderMobileComparisonTable(featureSections, {density: 'compact'})}
          {renderDesktopComparisonTable(featureSections, {density: 'compact'})}
        </div>
      </div>
    )
  }

  const renderPluginTables = () => {
    return (
      // scroll-mt keeps the heading clear of the sticky plan-name header
      <div className="scroll-mt-24 bg-white" id={PLUGIN_SECTION_ID}>
        <p className="mb-4 mt-36 px-4 text-center">
          <a
            className="inline-flex items-center gap-1 text-sm text-gray-600 underline decoration-gray-300 decoration-dashed decoration-2 underline-offset-[3px] hover:text-primary"
            href={`#${FEATURE_SECTION_ID}`}
            onClick={scrollToAnchor(FEATURE_SECTION_ID)}
          >
            <ArrowSmUpIcon className="h-4 w-4" aria-hidden="true" />
            Back to feature comparison
          </a>
        </p>
        <h1 className="mb-4 px-4 text-center text-4xl font-extrabold text-gray-900">
          Plugin Comparison
        </h1>
        <p className="mb-8 px-4 text-center text-lg text-gray-600">
          Tell us where to send your form submissions. We'll handle the rest.
        </p>
        {renderTableBillingToggle()}
        <div className="mx-auto mb-16 bg-white lg:max-w-7xl lg:px-8">
          {renderMobileComparisonTable(pluginSections, {
            buttonPrefix: 'mobile-plugins-',
            density: 'tight',
            subtitles: PLUGIN_TABLE_SUBTITLES,
          })}
          {renderDesktopComparisonTable(pluginSections, {
            caption: 'Plugin availability by plan',
            buttonPrefix: 'plugins-table-header-',
            footerButtonPrefix: 'plugins-table-footer-',
            density: 'tight',
            subtitles: PLUGIN_TABLE_SUBTITLES,
          })}
        </div>
      </div>
    )
  }

  const renderFreeForever = () => {
    return (
      <div className="relative mx-auto mt-4 max-w-7xl px-4 sm:px-6 lg:mt-5 lg:px-8">
        <div className="mx-auto max-w-md lg:max-w-5xl">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-8 shadow-sm sm:p-10 lg:flex lg:items-start">
            <div className="flex-1">
              <div>
                <h3 className="bg-white text-lg font-semibold uppercase tracking-wide text-gray-800">
                  Free Forever
                </h3>
              </div>
              <div className="mb-8 mr-2 mt-4 text-gray-600 lg:mb-4 lg:w-3/4">
                Use Formspree for testing and development,
                <br className="hidden sm:inline" /> limited to{' '}
                <strong>50</strong> submissions/mo.
              </div>
            </div>
            <CheckoutButton prod={getProduct('free')} fullWidth={false} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <h1 className="my-16 px-4 text-5xl font-extrabold text-gray-900 sm:px-6 sm:text-center lg:px-8">
        Formspree Plans
      </h1>

      {account?.product === 'free' &&
        account?.flags?.satisfaction_guarantee_group === true && (
          <div className="mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:px-8">
            <PromoBanner
              icon={
                <img
                  src={formspreeStarSvg}
                  alt=""
                  aria-hidden="true"
                  width={100}
                  height={77}
                />
              }
              title="Try Formspree today risk free!"
              description={
                <p className="text-white/90">
                  Upgrade today and cancel for a full refund any time within the
                  first month
                </p>
              }
            />
          </div>
        )}

      <div className="bg-white">
        <div className="mx-auto max-w-7xl bg-watermark bg-left-top bg-no-repeat px-4 sm:px-6 lg:px-8">
          <div className="sm:align-center sm:flex sm:flex-col">
            {/* What the reader came for takes the subtitle's place rather than
                stacking under a pitch they did not ask to read. Same slot and
                type scale, so the page keeps its shape either way. */}
            <p className="text-xl text-gray-600 sm:text-center">
              {subheadingMode === 'upgrade' ? (
                <>
                  Upgrade to {emphasize(derivedProduct.name)} to get{' '}
                  {markedRowLink}
                </>
              ) : subheadingMode === 'entitled' ? (
                <>
                  {planIncludesIt && accountPlanProduct !== undefined ? (
                    <>
                      Your {emphasize(accountPlanProduct.name)} plan includes{' '}
                    </>
                  ) : (
                    <>Your account includes </>
                  )}
                  {markedRowLink}
                </>
              ) : subheadingMode === 'included' ? (
                <>
                  {markedRowLink} is included in{' '}
                  {emphasize(derivedProduct.name)} and up
                </>
              ) : (
                <>
                  Whether you're building a personal website or managing client
                  projects,
                  <br className="hidden sm:inline" /> Formspree has an
                  affordable plan to meet your needs.
                </>
              )}
            </p>

            {account && isTrialPlan(account.plan) ? (
              <h3 className="mt-8 text-center text-xl font-bold text-blue-700">
                To continue the features of your trial, select the{' '}
                {featureTrialMinimumProduct(account.product).name} or higher
                plan.
              </h3>
            ) : null}

            {renderBillingPeriodToggle('mt-10 self-center sm:mt-14', 'large')}
          </div>
          {account && isLegacyPlan(account.plan) ? (
            <Alert type="warning" className={'mt-8 text-center'}>
              <div className="text-l text-center text-yellow-700">
                You're in a legacy {getProduct(account.product).name} plan. Plan
                limits below only apply if you switch to the latest plans.{' '}
                <a
                  href="https://help.formspree.io/hc/articles/360023105693-Why-am-I-in-a-legacy-plan"
                  target="_blank"
                  className="font-bold"
                >
                  Learn more →
                </a>
              </div>
            </Alert>
          ) : null}
          <div className="my-12 mt-8 space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:mx-0 lg:max-w-none lg:grid-cols-4">
            {products
              .filter(prod => SHOW_FREE || prod.name !== 'Free')
              .map(prod => renderProduct(prod))}
          </div>
          {renderFreeForever()}
        </div>
      </div>

      {renderComparisonTables()}
      {renderPluginTables()}
    </>
  )
}

function getFeaturesPlusTitle(product) {
  switch (product.id) {
    case 'personal':
      return "What's included:"
    case 'professional':
      return 'All Personal plus:'
    case 'business':
      return 'All Professional plus:'
    case 'custom':
      return 'All Business plus:'
    default:
      Sentry.captureException(new Error(`Unknown product id: ${product.id}`))
      return ''
  }
}

export default function () {
  return (
    <CheckoutContextProvider>
      <PlansPage />
    </CheckoutContextProvider>
  )
}
